import { useCallback, useEffect, useState } from "react";

import { addKey, timezone } from "@/utils";

import { useAppDispatch } from "../useRedux";

import * as actions from "@/redux/LandingPageReducer";

const usePromoCode = ({ methods, packData, additionalData = {} }) => {
  const dispatch = useAppDispatch();

  const { getValues, setError, setValue, clearErrors, handleSubmit } = methods;

  const [promoCodeButtonText, setPromoCodeButtonText] = useState("APPLY");
  const [isPromoCodeLoading, setIsPromoCodeLoading] = useState(false);
  const [isPromoCodeApplied, setIsPromoCodeApplied] = useState(false);
  const [promoCodeData, setPromoCodeData] = useState(null);
  const [promoCodeError, setPromoCodeError] = useState(null);

  const preApplyPromoCode = (couponCode: string) => {
    setValue("promoCode", couponCode);
    const { promoCode } =
      getValues();

    clearErrors("promoCode");
    // if (!promoCode && !couponCode) return;

    setIsPromoCodeLoading(true);

    const data = {
      coupon_code: promoCode ? promoCode?.toUpperCase() : couponCode?.toUpperCase(),
      ...addKey("slug", packData?.packageSlug),
      ...addKey("package_slug", packData?.purchasedPlanSlug),
      timezone,
      ...additionalData,
    };

    dispatch(actions.promoCodeCheckRequest(data)).then((res) => {
      setIsPromoCodeLoading(false);
      const error = res?.error || !res?.payload?.status;
      if (error) {
        const errorMessage = res?.error?.message || res?.payload?.message;
        setPromoCodeError(errorMessage);
        setError("promoCode", { type: "custom", message: errorMessage });
      } else {
        setPromoCodeData(res?.payload || null);
        setIsPromoCodeApplied(true);
        setPromoCodeButtonText("REMOVE");
      }
    });
  }

  const applyPromoCode = handleSubmit(() => {
    const { mobile, mobileCountryCode, promoCode, nutritionGoalID } =
      getValues();

    clearErrors("promoCode");
    if (!promoCode) return;

    setIsPromoCodeLoading(true);

    const data = {
      coupon_code: promoCode?.toUpperCase(),
      mobile: mobile,
      phone_prefix: mobileCountryCode,
      ...addKey("nutrition_goal_id", nutritionGoalID),
      ...addKey("slug", packData?.packageSlug),
      ...addKey("package_slug", packData?.purchasedPlanSlug),
      timezone,
      ...additionalData,
    };

    dispatch(actions.promoCodeCheckRequest(data)).then((res) => {
      setIsPromoCodeLoading(false);
      const error = res?.error || !res?.payload?.status;
      if (error) {
        const errorMessage = res?.error?.message || res?.payload?.message;
        setPromoCodeError(errorMessage);
        setError("promoCode", { type: "custom", message: errorMessage });
      } else {
        setPromoCodeData(res?.payload || null);
        setIsPromoCodeApplied(true);
        setPromoCodeButtonText("REMOVE");
      }
    });
  });

  const removeAppliedPromoCode = useCallback((clearField = true) => {
    if(clearField){
      setValue("promoCode", "");
    }
    clearErrors("promoCode");
    setPromoCodeButtonText("APPLY");
    setIsPromoCodeApplied(false);
    setPromoCodeData(null);
  }, [setValue, clearErrors]);

  const handlePromoCodeChange = () => {
    clearErrors("promoCode");
    setPromoCodeButtonText("APPLY");
    setIsPromoCodeApplied(false);
    setPromoCodeData(null);
  };

  useEffect(() => {
    return () => removeAppliedPromoCode();
  }, [removeAppliedPromoCode]);

  return {
    isPromoCodeLoading,
    isPromoCodeApplied,
    promoCodeData,
    promoCodeError,
    handlePromoCodeChange,
    applyPromoCode,
    removeAppliedPromoCode,
    promoCodeButtonText,
    preApplyPromoCode
  };
};

export default usePromoCode;
